// src/pages/Chat/ChatPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChatSidebar from './ChatSidebar';
import ChatWindow from './ChatWindow';
import { getUserChats, createChat as apiCreateChat } from '../../services/chatApi';
import { Menu, X } from 'lucide-react'; 

console.log('ChatWindow component:', ChatWindow);

const MIN_SIDEBAR_WIDTH = 50; // min width in pixels
const MAX_SIDEBAR_WIDTH = 400; // max width in pixels
const DEFAULT_SIDEBAR_WIDTH = 280; // default width
const MOBILE_BREAKPOINT = 768; // screen width in pixels to switch to mobile view

const ChatPage = () => {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { t } = useTranslation();

    const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
    const [isResizing, setIsResizing] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < MOBILE_BREAKPOINT);
    
    const sidebarRef = useRef(null);

    console.log('=== ChatPage Render ===');
    console.log('chatId from params:', chatId);
    console.log('window.location.pathname:', window.location.pathname);

    const checkScreenSize = () => {
        // We only automatically collapse on initial load or resize if entering mobile view
        if (window.innerWidth < MOBILE_BREAKPOINT) {
            setIsCollapsed(true);
        }
    };

    useEffect(() => {
        window.addEventListener('resize', checkScreenSize);
        checkScreenSize(); // Initial check
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);


    // --- Resizing Logic ---
    const startResizing = useCallback((mouseDownEvent) => {
        setIsResizing(true);
        mouseDownEvent.preventDefault(); // Prevent text selection while dragging
    }, []);

    const stopResizing = useCallback(() => {
        setIsResizing(false);
    }, []);

    const resize = useCallback((mouseMoveEvent) => {
        if (isResizing) {
            let newWidth = mouseMoveEvent.clientX - (sidebarRef.current?.getBoundingClientRect().left || 0);
            
            // Apply constraints
            if (newWidth < MIN_SIDEBAR_WIDTH) {
                newWidth = MIN_SIDEBAR_WIDTH;
            }
            if (newWidth > MAX_SIDEBAR_WIDTH) {
                newWidth = MAX_SIDEBAR_WIDTH;
            }
            setSidebarWidth(newWidth);
        }
    }, [isResizing]);

    useEffect(() => {
        window.addEventListener('mousemove', resize);
        window.addEventListener('mouseup', stopResizing);

        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [resize, stopResizing]);

    

    useEffect(() => {
        const fetchChats = async () => {
            try {
                setLoading(true);
                const userChats = await getUserChats();
                setChats(userChats);
                setError(null);
            } catch (err) {
                setError('Failed to load chats. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchChats();
    }, []);

    const handleCreateChat = async () => {
        // Find if any chat has the default title
        const existingNewChat = chats.find(chat => chat.title === 'New Chat');

        // This is the "Smart Create" logic
        if (existingNewChat) {
            console.log(`An unused "New Chat" already exists (ID: ${existingNewChat.chat_id}). Navigating to it.`);
            navigate(`/chat/${existingNewChat.chat_id}`);
            if (isCollapsed) setIsCollapsed(false);
            return; // Exit the function
        }
        
        // This code now only runs if no "New Chat" exists, OR if the user was already on one
        try {
            console.log("Creating a new chat via API...");
            const newChat = await apiCreateChat();
            console.log("New chat created:", newChat);

            setChats(prevChats => [newChat, ...prevChats]);
            
            navigate(`/chat/${newChat.chat_id}`);
            if (isCollapsed) setIsCollapsed(false);
        } catch (err) {
            console.error('Failed to create new chat:', err);
            setError(t('chat.errors.createChat'));
        }
    };
    
    const handleDeleteChat = (deletedChatId) => {
        const isDeletingActiveChat = chatId === deletedChatId;
        const remainingChats = chats.filter(chat => chat.chat_id !== deletedChatId);
        setChats(remainingChats);

        if (isDeletingActiveChat) {
            if (remainingChats.length > 0) {
                navigate(`/chat/${remainingChats[0].chat_id}`);
            } else {
                navigate('/chat'); 
            }
        }
    };
    
    const handleTitleUpdate = (updatedChatId, newTitle) => {
        console.log(`(ChatPage) Received title update for ${updatedChatId}: "${newTitle}"`);
        setChats(prevChats =>
            prevChats.map(chat =>
                chat.chat_id === updatedChatId
                    ? { ...chat, title: newTitle }
                    : chat
            )
        );
    };

    const handleSelectChat = () => {
        // On mobile, close the sidebar when a chat is selected
        if (window.innerWidth < MOBILE_BREAKPOINT) {
            setIsCollapsed(true);
        }
    };

    return (
        <div className="relative flex h-full w-full overflow-hidden bg-gray-100">
            {/* --- NEW: Backdrop for mobile overlay --- */}
            {!isCollapsed && (
                <div
                    onClick={() => setIsCollapsed(true)}
                    className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
                    aria-hidden="true"
                />
            )}

            {/* --- MODIFIED: Sidebar Container for Mobile Overlay & Desktop Push --- */}
            <div
                ref={sidebarRef}
                className={`
                    flex-shrink-0 bg-brand-white
                    transition-transform duration-300 ease-in-out
                    
                    // Mobile overlay styles (default)
                    fixed top-0 left-0 h-full z-30 
                    ${isCollapsed ? '-translate-x-full' : 'translate-x-0'}
                    
                    // Desktop styles (override mobile)
                    md:relative md:translate-x-0 md:flex
                `}
                style={{ width: `${sidebarWidth}px` }} // This width is used for desktop resizing
            >
                <ChatSidebar
                    chats={chats}
                    activeChatId={chatId}
                    onCreateChat={handleCreateChat}
                    onDeleteChat={handleDeleteChat}
                    onSelectChat={handleSelectChat} // Pass this down
                    loading={loading}
                    error={error}
                />
            </div>

            {/* --- MODIFIED: Resizer only for Desktop --- */}
            <div
                onMouseDown={startResizing}
                className="hidden md:block w-1.5 cursor-col-resize bg-gray-300 hover:bg-brand-orange transition-colors"
            />

            {/* --- MODIFIED: Mobile Toggle Button --- */}
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="md:hidden absolute top-4 left-4 z-40 p-2 bg-brand-orange rounded-md shadow-md"
                aria-label="Toggle sidebar"
            >
                {/* The button is visible even when the menu is open, so it can be closed */}
                {isCollapsed ? <Menu size={20} /> : <X size={20} />}
            </button>
            
            <main className="flex-1 flex flex-col min-h-0">
                <ChatWindow 
                    key={chatId || 'no-chat'} 
                    chatId={chatId} 
                    onTitleUpdate={handleTitleUpdate}
                    onDeleteChat={handleDeleteChat}
                    onCreateChat={handleCreateChat}
                />
            </main>
        </div>
    );
};

export default ChatPage;

























































